import { useAuthenticator } from "@aws-amplify/ui-react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useToken } from "providers/token";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  apiBaseUrlForHandoff,
  knowledgeAuthContinueStorageKey,
  parseKnowledgeAuthContinueParams,
  type KnowledgeAuthContinueParams,
} from "utils/knowledgeAuthContinue";

function readStoredContinueParams(): KnowledgeAuthContinueParams | null {
  try {
    const raw = sessionStorage.getItem(knowledgeAuthContinueStorageKey());
    if (raw == null || raw.length === 0) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed == null) {
      return null;
    }
    return parseKnowledgeAuthContinueParams({
      returnTo: Reflect.get(parsed, "returnTo") as string | undefined,
      state: Reflect.get(parsed, "state") as string | undefined,
      knowledgeOrigin: Reflect.get(parsed, "knowledgeOrigin") as string | undefined,
    });
  } catch {
    return null;
  }
}

function storeContinueParams(params: KnowledgeAuthContinueParams): void {
  sessionStorage.setItem(knowledgeAuthContinueStorageKey(), JSON.stringify(params));
}

function clearContinueParams(): void {
  sessionStorage.removeItem(knowledgeAuthContinueStorageKey());
}

async function mintHandoffCode(idToken: string, state: string): Promise<string | null> {
  const response = await fetch(`${apiBaseUrlForHandoff()}/api/auth/knowledge-handoff`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ state }),
  });
  if (!response.ok) {
    return null;
  }
  const json: unknown = await response.json();
  const code = typeof json === "object" && json != null ? Reflect.get(json, "code") : undefined;
  if (typeof code !== "string" || code.length === 0) {
    return null;
  }
  return code;
}

function redirectToKnowledgeAccept(params: KnowledgeAuthContinueParams, code: string): void {
  clearContinueParams();
  const accept = new URL("/auth/accept", params.knowledgeOrigin);
  accept.searchParams.set("code", code);
  accept.searchParams.set("state", params.state);
  window.location.assign(accept.toString());
}

/**
 * After Oak login, mint a Valkey handoff code and send the browser back to Knowledge.
 * Mounted under AuthProvider so unauthenticated users see SignInForm first.
 */
export function KnowledgeAuthContinue() {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const { getIdTokenRefreshIfExpired } = useToken();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const startedRef = useRef(false);

  const resolveParams = useCallback((): KnowledgeAuthContinueParams | null => {
    const fromQuery = parseKnowledgeAuthContinueParams({
      returnTo: searchParams.get("return_to"),
      state: searchParams.get("state"),
      knowledgeOrigin: searchParams.get("knowledge_origin"),
    });
    if (fromQuery != null) {
      storeContinueParams(fromQuery);
      return fromQuery;
    }
    return readStoredContinueParams();
  }, [searchParams]);

  useEffect(() => {
    resolveParams();
  }, [resolveParams]);

  useEffect(() => {
    if (authStatus !== "authenticated" || startedRef.current) {
      return;
    }
    startedRef.current = true;

    void (async () => {
      const params = resolveParams();
      if (params == null) {
        setError("Missing or invalid Knowledge login parameters.");
        return;
      }

      const idToken = await getIdTokenRefreshIfExpired();
      if (idToken.length === 0) {
        setError("Could not read your Encamp session. Try signing in again.");
        startedRef.current = false;
        return;
      }

      try {
        const code = await mintHandoffCode(idToken, params.state);
        if (code == null) {
          setError("Could not create a Knowledge login handoff. Try again.");
          startedRef.current = false;
          return;
        }
        redirectToKnowledgeAccept(params, code);
      } catch {
        setError("Could not reach the API to finish Knowledge login.");
        startedRef.current = false;
      }
    })();
  }, [authStatus, getIdTokenRefreshIfExpired, resolveParams]);

  if (error != null) {
    return (
      <Box sx={{ p: 4, maxWidth: 480, mx: "auto" }}>
        <Typography color="error" gutterBottom>
          {error}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Return to Knowledge and try signing in again, or open Encamp normally.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "40vh",
        gap: 2,
      }}
    >
      <CircularProgress size={32} />
      <Typography color="text.secondary">Continuing to Knowledge…</Typography>
    </Box>
  );
}
